/**
 * /get smart-link landing — install path for the GoodShowHQ mobile app.
 *
 * Translated from the Tailwind + lucide-react reference at
 * `design/pages/marketing/MobileLanding.jsx` into MUI 7 + @mui/icons-material.
 * The reference file is the source of truth for layout, copy, and
 * behaviour — when this file disagrees with the reference, the
 * reference wins.
 *
 * Reachable from:
 *   - desktop tray "Faster on mobile" plug (TrayPanel)
 *   - in-app mobile-app pitches inside the web dashboard
 *   - marketing + direct sharing
 *
 * Public route — no auth gate, no nav-bar entry. The detected device
 * (desktop / iOS / Android) determines the install CTA card; everything
 * else (hero copy, phone mockup, feature grid, footer) is identical.
 *
 * NOTE on URL: the web app uses HashRouter, so this page lives at
 * `https://goodshowhq.app/#/get` at runtime. The QR code encodes the
 * page's actual URL via `window.location.href` so a phone scan
 * always lands here, regardless of whether a server-side rewrite
 * for `/get → /#/get` is in place. TODO(infra): add a hosting
 * rewrite from /get → /#/get and switch the QR to the canonical
 * https://goodshowhq.app/get once the rewrite is live.
 */
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
  Box,
  Button,
  Container,
  Stack,
  ThemeProvider,
  Typography,
} from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import AutoAwesomeRounded from '@mui/icons-material/AutoAwesomeRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import AppleRounded from '@mui/icons-material/Apple';
import SendRounded from '@mui/icons-material/SendRounded';
import LayersRounded from '@mui/icons-material/LayersRounded';
import NotificationsRounded from '@mui/icons-material/NotificationsRounded';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import PlayCircleRounded from '@mui/icons-material/PlayCircleRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import RotateRightRounded from '@mui/icons-material/RotateRightRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import {QRCodeSVG} from 'qrcode.react';
import {goodShowHQTheme} from '../../theme/planapusTheme';
import logoMark from '../../assets/brand/logo-mark.svg';
import BrandChip from '../common/BrandChip';
import {
  AppleIcon,
  HomeAssistantIcon,
  LinuxIcon,
  WindowsIcon,
} from '../common/PlatformIcons';

// -- Local colour constants (mirroring LandingPage.tsx) --------------------

const C = {
  white: '#FFFFFF',
  slate50: '#F8FAFC',
  slate100: '#F1F5F9',
  slate200: '#E2E8F0',
  slate400: '#94A3B8',
  slate500: '#64748B',
  slate600: '#475569',
  slate700: '#334155',
  slate800: '#1E293B',
  slate900: '#0F172A',
  indigo100: '#E0E7FF',
  indigo500: '#6366F1',
  indigo600: '#4F46E5',
  indigo700: '#4338CA',
  indigo800: '#3730A3',
  emerald200: '#A7F3D0',
  emerald500: '#10B981',
  emerald600: '#059669',
  rose200: '#FECDD3',
  rose500: '#F43F5E',
  rose600: '#E11D48',
  amber50: '#FFFBEB',
  orange300: '#FDBA74',
  orange400: '#FB923C',
};

// TODO(stores): replace placeholder hrefs with the canonical store
// URLs once the apps are published. The exact link format is:
//   iOS:     https://apps.apple.com/app/idXXXXXXXXX
//   Android: https://play.google.com/store/apps/details?id=com.goodshowhq.app
const APP_STORE_URL = 'https://apps.apple.com/app/id6772217916';
const PLAY_STORE_URL = '#';

// -- Logo (matches the LandingPage chrome) ---------------------------------

function Logo(): React.JSX.Element {
  return (
    <Box
      component="a"
      href="/"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        textDecoration: 'none',
      }}
    >
      <Box
        component="img"
        src={logoMark}
        alt=""
        aria-hidden
        sx={{height: 28, width: 28, display: 'block'}}
      />
      <Typography
        component="span"
        sx={{
          fontSize: 16,
          fontWeight: 800,
          letterSpacing: '-0.01em',
          color: C.slate900,
        }}
      >
        GoodShowHQ
      </Typography>
    </Box>
  );
}

// -- App Store / Play Store badges -----------------------------------------
//
// TODO(branding): swap these inline reproductions for the official
// Apple "Download on the App Store" and Google "Get it on Google Play"
// SVG badges before public launch. Both companies require specific
// colours, padding, and aspect ratios — the inline versions ship
// faster but aren't store-policy-compliant.
//   Apple:  https://developer.apple.com/app-store/marketing/guidelines/
//   Google: https://play.google.com/intl/en_us/badges/

interface BadgeProps {
  size?: 'sm' | 'lg';
  href?: string;
}

function AppStoreBadge({size = 'lg', href = APP_STORE_URL}: BadgeProps): React.JSX.Element {
  const big = size === 'lg';
  return (
    <Box
      component="a"
      href={href}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1.5,
        borderRadius: '12px',
        bgcolor: C.slate900,
        textDecoration: 'none',
        transition: 'background-color 120ms',
        px: big ? 2.5 : 2,
        py: big ? 1.5 : 1,
        '&:hover': {bgcolor: C.slate800},
      }}
    >
      <AppleRounded
        sx={{
          color: C.white,
          fontSize: big ? 28 : 20,
        }}
      />
      <Box sx={{textAlign: 'left', color: C.white}}>
        <Typography sx={{fontSize: big ? 10 : 9, lineHeight: 1}}>
          Download on the
        </Typography>
        <Typography
          sx={{
            mt: 0.25,
            fontSize: big ? 18 : 16,
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          App Store
        </Typography>
      </Box>
    </Box>
  );
}

function PlayStoreBadge({size = 'lg', href = PLAY_STORE_URL}: BadgeProps): React.JSX.Element {
  const big = size === 'lg';
  return (
    <Box
      component="a"
      href={href}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1.5,
        borderRadius: '12px',
        bgcolor: C.slate900,
        textDecoration: 'none',
        transition: 'background-color 120ms',
        px: big ? 2.5 : 2,
        py: big ? 1.5 : 1,
        '&:hover': {bgcolor: C.slate800},
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 24 24"
        aria-hidden
        sx={{height: big ? 28 : 20, width: big ? 28 : 20, display: 'block'}}
      >
        <path
          fill="#34A853"
          d="M3.609 1.814 13.792 12 3.609 22.186a1.6 1.6 0 0 1-.61-1.235V3.05c0-.482.241-.927.61-1.236Z"
        />
        <path
          fill="#FBBC04"
          d="m13.792 12 3.273-3.273 3.272 1.886c.927.535.927 1.879 0 2.414l-3.272 1.886L13.792 12Z"
        />
        <path
          fill="#EA4335"
          d="M17.065 8.727 13.792 12 3.609 1.814A1.6 1.6 0 0 1 5.46 1.69l11.605 7.037Z"
        />
        <path
          fill="#4285F4"
          d="M17.065 15.273 5.46 22.31a1.6 1.6 0 0 1-1.851-.124L13.792 12l3.273 3.273Z"
        />
      </Box>
      <Box sx={{textAlign: 'left', color: C.white}}>
        <Typography sx={{fontSize: big ? 10 : 9, lineHeight: 1}}>
          GET IT ON
        </Typography>
        <Typography
          sx={{
            mt: 0.25,
            fontSize: big ? 18 : 16,
            fontWeight: 600,
            lineHeight: 1,
          }}
        >
          Google Play
        </Typography>
      </Box>
    </Box>
  );
}

// -- Country picker (slim version of the AuthPage picker) ------------------
//
// TODO(refactor): extract a shared CountryPicker from
// components/Auth/AuthPage.tsx (which has a richer COUNTRIES list +
// click-outside dismiss) once a second consumer makes it worthwhile.
// For now, ship a minimal SMS-friendly subset — the send-link flow
// is itself a stub until the SMS provider is wired up server-side.

interface SmsCountry {
  code: string;
  flag: string;
  dial: string;
}

const SMS_COUNTRIES: readonly SmsCountry[] = [
  {code: 'US', flag: '🇺🇸', dial: '+1'},
  {code: 'CA', flag: '🇨🇦', dial: '+1'},
  {code: 'GB', flag: '🇬🇧', dial: '+44'},
  {code: 'AU', flag: '🇦🇺', dial: '+61'},
  {code: 'IN', flag: '🇮🇳', dial: '+91'},
  {code: 'AE', flag: '🇦🇪', dial: '+971'},
];

function CountryPicker({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (code: string) => void;
  disabled?: boolean;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const country = SMS_COUNTRIES.find(c => c.code === value) ?? SMS_COUNTRIES[0];
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <Box ref={ref} sx={{position: 'relative'}}>
      <Button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        sx={{
          height: 36,
          minWidth: 0,
          gap: 0.5,
          px: 1.25,
          borderRadius: '8px 0 0 8px',
          border: `1px solid ${C.slate200}`,
          borderRight: 'none',
          bgcolor: C.white,
          fontSize: 13,
          fontWeight: 600,
          color: C.slate700,
          textTransform: 'none',
          '&:hover': {bgcolor: C.slate50, borderColor: C.slate200},
        }}
      >
        <Box component="span" sx={{fontSize: 14, lineHeight: 1}}>
          {country.flag}
        </Box>
        <Box component="span" sx={{fontVariantNumeric: 'tabular-nums'}}>
          {country.dial}
        </Box>
        <ExpandMoreRounded sx={{fontSize: 14, color: C.slate400}} />
      </Button>
      {open && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: '100%',
            zIndex: 10,
            mt: 0.5,
            width: 200,
            overflowY: 'auto',
            borderRadius: 2,
            border: `1px solid ${C.slate200}`,
            bgcolor: C.white,
            py: 0.5,
            boxShadow: '0 10px 25px rgba(15,23,42,0.10)',
          }}
        >
          {SMS_COUNTRIES.map(c => (
            <Box
              key={c.code}
              component="button"
              type="button"
              onClick={() => {
                onChange(c.code);
                setOpen(false);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                px: 1.5,
                py: 0.75,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                color: C.slate700,
                textAlign: 'left',
                '&:hover': {bgcolor: C.slate50},
              }}
            >
              <Box component="span" sx={{fontSize: 14}}>
                {c.flag}
              </Box>
              <Box component="span" sx={{flex: 1}}>
                {c.code}
              </Box>
              <Box
                component="span"
                sx={{
                  color: C.slate500,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {c.dial}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// -- Phone mockup ----------------------------------------------------------

function PhoneMockup(): React.JSX.Element {
  return (
    <Box sx={{position: 'relative', mx: 'auto', width: 260}}>
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '40px',
          border: `7px solid ${C.slate900}`,
          bgcolor: C.white,
          boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.45)',
        }}
      >
        {/* Notch */}
        <Box
          sx={{
            position: 'absolute',
            left: '50%',
            top: 0,
            zIndex: 50,
            height: 20,
            width: 96,
            transform: 'translateX(-50%)',
            borderBottomLeftRadius: 16,
            borderBottomRightRadius: 16,
            bgcolor: C.slate900,
          }}
        />
        <Box sx={{position: 'relative', height: 520, bgcolor: C.slate50}}>
          {/* Status bar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2.5,
              pb: 0.5,
              pt: 1,
              fontSize: 10,
              fontWeight: 600,
              color: C.slate900,
            }}
          >
            <span>9:41</span>
            <span>●●●●</span>
          </Box>
          {/* Toolbar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 28,
                width: 28,
                borderRadius: '50%',
                bgcolor: C.white,
              }}
            >
              <CloseRounded sx={{fontSize: 12, color: C.slate700}} />
            </Box>
            <Typography sx={{fontSize: 11, fontWeight: 700, color: C.slate900}}>
              12{' '}
              <Box component="span" sx={{fontWeight: 400, color: C.slate400}}>
                of 47
              </Box>
            </Typography>
            <Box sx={{width: 28}} />
          </Box>
          {/* Progress bar */}
          <Box
            sx={{
              mx: 2,
              height: 2,
              overflow: 'hidden',
              borderRadius: 999,
              bgcolor: C.slate200,
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: '16.6%',
                borderRadius: 999,
                bgcolor: C.indigo600,
              }}
            />
          </Box>
          {/* Card stack */}
          <Box sx={{position: 'relative', mx: 2, mt: 1.5, height: 300}}>
            <Box
              sx={{
                position: 'absolute',
                left: 16,
                right: 16,
                top: 8,
                height: '100%',
                transform: 'scale(0.95)',
                borderRadius: '16px',
                border: `1px solid ${C.slate200}`,
                bgcolor: C.white,
                opacity: 0.7,
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                left: 8,
                right: 8,
                top: 4,
                height: '100%',
                borderRadius: '16px',
                border: `1px solid ${C.slate200}`,
                bgcolor: C.white,
                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
              }}
            />
            <Box
              sx={{
                position: 'relative',
                height: '100%',
                overflow: 'hidden',
                borderRadius: '16px',
                border: `1px solid ${C.slate200}`,
                bgcolor: C.white,
                boxShadow: '0 4px 6px -1px rgba(15, 23, 42, 0.10)',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  height: 128,
                  background: `linear-gradient(135deg, ${C.orange300} 0%, ${C.rose500} 60%, ${C.rose600} 100%)`,
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PlayCircleRounded
                    sx={{height: 40, width: 40, color: 'rgba(255,255,255,0.9)'}}
                  />
                </Box>
              </Box>
              <Box sx={{p: 1.5}}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                    borderRadius: 999,
                    bgcolor: C.indigo100,
                    px: 0.75,
                    py: 0.25,
                    fontSize: 8,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    color: C.indigo700,
                  }}
                >
                  <AutoAwesomeRounded sx={{fontSize: 8}} />
                  Score this
                </Box>
                <Typography
                  sx={{
                    mt: 0.75,
                    fontSize: 13,
                    fontWeight: 800,
                    lineHeight: 1.15,
                    color: C.slate900,
                  }}
                >
                  How volcanoes actually work
                </Typography>
                <Typography sx={{fontSize: 10, color: C.slate500}}>
                  SciShow Kids
                </Typography>
                <Box
                  sx={{
                    mt: 1,
                    borderRadius: '8px',
                    bgcolor: C.slate50,
                    p: 1,
                  }}
                >
                  <Typography
                    sx={{fontSize: 10, lineHeight: 1.4, color: C.slate700}}
                  >
                    Good fit for Yusuf?
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          {/* Action buttons */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 24,
              px: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 48,
                  width: 48,
                  borderRadius: '50%',
                  border: `2px solid ${C.rose200}`,
                  bgcolor: C.white,
                  boxShadow: '0 4px 6px -1px rgba(15, 23, 42, 0.10)',
                }}
              >
                <CloseRounded sx={{fontSize: 20, color: C.rose500}} />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 36,
                  width: 36,
                  borderRadius: '50%',
                  border: `1px solid ${C.slate200}`,
                  bgcolor: C.white,
                  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
                }}
              >
                <RotateRightRounded sx={{fontSize: 14, color: C.slate400}} />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 48,
                  width: 48,
                  borderRadius: '50%',
                  border: `2px solid ${C.emerald200}`,
                  bgcolor: C.white,
                  boxShadow: '0 4px 6px -1px rgba(15, 23, 42, 0.10)',
                }}
              >
                <CheckRounded sx={{fontSize: 20, color: C.emerald500}} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Floating swipe-direction pills */}
      <Box
        sx={{
          position: 'absolute',
          left: -12,
          top: '50%',
          transform: 'translateY(-50%)',
          borderRadius: 999,
          border: `1px solid ${C.rose200}`,
          bgcolor: C.white,
          px: 1.25,
          py: 0.75,
          boxShadow: '0 4px 6px -1px rgba(15, 23, 42, 0.10)',
        }}
      >
        <Typography
          sx={{
            fontSize: 9,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: C.rose600,
          }}
        >
          ← Block
        </Typography>
      </Box>
      <Box
        sx={{
          position: 'absolute',
          right: -12,
          top: '50%',
          transform: 'translateY(-50%)',
          borderRadius: 999,
          border: `1px solid ${C.emerald200}`,
          bgcolor: C.white,
          px: 1.25,
          py: 0.75,
          boxShadow: '0 4px 6px -1px rgba(15, 23, 42, 0.10)',
        }}
      >
        <Typography
          sx={{
            fontSize: 9,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: C.emerald600,
          }}
        >
          Approve →
        </Typography>
      </Box>
    </Box>
  );
}

// -- CTA cards (one per detected device) -----------------------------------

type Device = 'desktop' | 'ios' | 'android';

function DesktopCTA(): React.JSX.Element {
  const [emailMode, setEmailMode] = useState(false);
  const [country, setCountry] = useState<string>('US');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  // The QR encodes the page's actual runtime URL so a phone scan
  // always lands on /get under whichever router config (HashRouter
  // today, BrowserRouter once a server-side rewrite lands).
  const qrValue = useMemo(() => {
    if (typeof window === 'undefined') return 'https://goodshowhq.app/get';
    return window.location.href;
  }, []);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (submitting) return;
    const country_obj = SMS_COUNTRIES.find(c => c.code === country) ?? SMS_COUNTRIES[0];
    const phoneNumber = `${country_obj.dial}${phone.trim().replace(/[^\d]/g, '')}`;
    setSubmitting(true);
    setFeedback(null);
    try {
      const res = await fetch('/api/send-app-link', {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({phoneNumber}),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      setFeedback('Sent! Check your phone.');
      setPhone('');
    } catch (err) {
      // Endpoint is a server-side stub today — surface a friendly
      // message either way and let the user fall back to the QR /
      // store badges if the request can't reach the function.
      setFeedback("Couldn't send — try the QR or the badges above.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        borderRadius: '24px',
        border: `1px solid ${C.slate200}`,
        bgcolor: C.white,
        p: 3,
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={2.5}>
        {/* QR */}
        <Box sx={{flexShrink: 0}}>
          <Box
            sx={{
              borderRadius: '16px',
              border: `1px solid ${C.slate200}`,
              bgcolor: C.white,
              p: 0.5,
              display: 'inline-flex',
            }}
          >
            <QRCodeSVG
              value={qrValue}
              size={176}
              level="M"
              marginSize={0}
              fgColor={C.slate900}
              bgColor={C.white}
            />
          </Box>
          <Typography
            sx={{
              mt: 1,
              textAlign: 'center',
              fontSize: 10,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: C.slate500,
            }}
          >
            Scan with your phone
          </Typography>
        </Box>
        {/* Text + alternatives */}
        <Box sx={{flex: 1, minWidth: 0}}>
          <Typography
            component="h3"
            sx={{
              fontSize: 16,
              fontWeight: 800,
              letterSpacing: '-0.01em',
              color: C.slate900,
            }}
          >
            Get GoodShowHQ on your phone
          </Typography>
          <Typography
            sx={{mt: 0.5, fontSize: 14, lineHeight: 1.5, color: C.slate600}}
          >
            Scan the QR with your phone's camera, or grab it directly from
            the store.
          </Typography>
          <Stack direction="row" spacing={1} sx={{mt: 2, flexWrap: 'wrap', gap: 1}}>
            <AppStoreBadge size="sm" />
            <PlayStoreBadge size="sm" />
          </Stack>
          <Box
            component="button"
            type="button"
            onClick={() => {
              setEmailMode(v => !v);
              setFeedback(null);
            }}
            sx={{
              mt: 2,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              color: C.indigo600,
              p: 0,
              '&:hover': {color: C.indigo800},
            }}
          >
            <SendRounded sx={{fontSize: 14}} />
            {emailMode ? 'Cancel' : 'Send link to your phone'}
          </Box>
          {emailMode && (
            <Box component="form" onSubmit={handleSubmit} sx={{mt: 1}}>
              <Stack direction="row" spacing={0}>
                <CountryPicker
                  value={country}
                  onChange={setCountry}
                  disabled={submitting}
                />
                <Box
                  component="input"
                  type="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPhone(e.target.value)
                  }
                  placeholder="Your phone number"
                  disabled={submitting}
                  required
                  sx={{
                    height: 36,
                    flex: 1,
                    minWidth: 0,
                    border: `1px solid ${C.slate200}`,
                    bgcolor: C.white,
                    px: 1.5,
                    fontSize: 14,
                    color: C.slate900,
                    outline: 'none',
                    '&:focus': {
                      borderColor: C.indigo500,
                      boxShadow: `0 0 0 2px ${C.indigo100}`,
                    },
                    '&::placeholder': {color: C.slate400},
                  }}
                />
                <Box
                  component="button"
                  type="submit"
                  disabled={submitting}
                  sx={{
                    height: 36,
                    px: 1.5,
                    borderRadius: '0 8px 8px 0',
                    border: 'none',
                    bgcolor: C.indigo600,
                    color: C.white,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    opacity: submitting ? 0.6 : 1,
                    '&:hover': {bgcolor: submitting ? C.indigo600 : C.indigo700},
                  }}
                >
                  {submitting ? 'Sending…' : 'Send'}
                </Box>
              </Stack>
              {feedback && (
                <Typography
                  sx={{
                    mt: 1,
                    fontSize: 11,
                    color: feedback.startsWith('Sent')
                      ? C.emerald600
                      : C.slate500,
                  }}
                >
                  {feedback}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

function MobileFallbackLink({
  href,
  children,
}: {
  href: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <Box
      component="a"
      href={href}
      sx={{
        mt: 2,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        textDecoration: 'none',
        fontSize: 12,
        fontWeight: 600,
        color: C.slate500,
        '&:hover': {color: C.slate900},
      }}
    >
      {children}
      <ArrowForwardRounded sx={{fontSize: 14}} />
    </Box>
  );
}

function IosCTA(): React.JSX.Element {
  return (
    <Box
      sx={{
        borderRadius: '24px',
        border: `1px solid ${C.slate200}`,
        bgcolor: C.white,
        p: 3,
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
      }}
    >
      <Typography
        component="h3"
        sx={{
          fontSize: 16,
          fontWeight: 800,
          letterSpacing: '-0.01em',
          color: C.slate900,
        }}
      >
        Get GoodShowHQ for iPhone
      </Typography>
      <Typography
        sx={{mt: 0.5, fontSize: 14, lineHeight: 1.5, color: C.slate600}}
      >
        Free. Works with your existing GoodShowHQ account.
      </Typography>
      <Box sx={{mt: 2}}>
        <AppStoreBadge size="lg" />
      </Box>
      <MobileFallbackLink href={PLAY_STORE_URL}>
        On Android? Get it on Google Play
      </MobileFallbackLink>
    </Box>
  );
}

function AndroidCTA(): React.JSX.Element {
  return (
    <Box
      sx={{
        borderRadius: '24px',
        border: `1px solid ${C.slate200}`,
        bgcolor: C.white,
        p: 3,
        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
      }}
    >
      <Typography
        component="h3"
        sx={{
          fontSize: 16,
          fontWeight: 800,
          letterSpacing: '-0.01em',
          color: C.slate900,
        }}
      >
        Get GoodShowHQ for Android
      </Typography>
      <Typography
        sx={{mt: 0.5, fontSize: 14, lineHeight: 1.5, color: C.slate600}}
      >
        Free. Works with your existing GoodShowHQ account.
      </Typography>
      <Box sx={{mt: 2}}>
        <PlayStoreBadge size="lg" />
      </Box>
      <MobileFallbackLink href={APP_STORE_URL}>
        On iOS? Get it on the App Store
      </MobileFallbackLink>
    </Box>
  );
}

// -- Feature grid ----------------------------------------------------------

interface Feature {
  Icon: React.ElementType;
  title: string;
  body: string;
}

// Verbatim from the reference — see design/pages/marketing/MobileLanding.jsx.
const features: Feature[] = [
  {
    Icon: LayersRounded,
    title: 'Quick-swipe queue',
    body:
      'Triage pending reviews in seconds — swipe right to approve, left to block.',
  },
  {
    Icon: NotificationsRounded,
    title: 'Notifications when reviews are waiting',
    body:
      "We'll ping you when GoodShowHQ needs your input. Open and decide in a tap.",
  },
  {
    Icon: TrendingUpRounded,
    title: 'See your impact on the go',
    body:
      "Streaks, weekly progress, and the channels you've curated for each kid.",
  },
];

// -- UA detection ----------------------------------------------------------

function detectDevice(): Device {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

// -- Page ------------------------------------------------------------------

export default function GetMobile(): React.JSX.Element {
  // SSR-safe: start with 'desktop' so a server-rendered HTML never
  // shows a half-cooked CTA, then switch on client mount when
  // navigator.userAgent is available.
  const [device, setDevice] = useState<Device>('desktop');
  useEffect(() => {
    setDevice(detectDevice());
  }, []);

  return (
    <ThemeProvider theme={goodShowHQTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          background: `linear-gradient(180deg, ${C.white} 0%, ${C.slate50} 100%)`,
          color: C.slate900,
          fontFamily:
            '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        }}
      >
        {/* Top bar */}
        <Box
          component="header"
          sx={{
            borderBottom: `1px solid ${C.slate200}`,
            bgcolor: 'rgba(255,255,255,0.7)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
          }}
        >
          <Container maxWidth="lg" sx={{px: 3}}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 2,
              }}
            >
              <Logo />
              <Box
                component="a"
                href="/"
                sx={{
                  textDecoration: 'none',
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.slate400,
                  '&:hover': {color: C.slate700},
                }}
              >
                ← Back to GoodShowHQ
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Hero */}
        <Container component="main" maxWidth="lg" sx={{px: 3, pt: 6, pb: 10}}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {xs: '1fr', lg: 'repeat(5, minmax(0, 1fr))'},
              alignItems: 'center',
              gap: {xs: 6, lg: 8},
            }}
          >
            <Box sx={{gridColumn: {lg: 'span 3'}}}>
              <BrandChip label="GoodShowHQ on iPhone & Android" />
              <Typography
                component="h1"
                sx={{
                  mt: 2,
                  fontSize: {xs: 36, md: 48},
                  fontWeight: 800,
                  lineHeight: 1.05,
                  letterSpacing: '-0.02em',
                  color: C.slate900,
                }}
              >
                Quick-swipe reviews from anywhere.
              </Typography>
              <Typography
                sx={{
                  mt: 2,
                  maxWidth: 560,
                  fontSize: 16,
                  lineHeight: 1.6,
                  color: C.slate600,
                }}
              >
                The fastest way to triage what GoodShowHQ has flagged for your
                kid. Swipe right to approve, left to block — and GoodShowHQ
                applies your input to YouTube directly.
              </Typography>

              <Box sx={{mt: 3.5}}>
                {device === 'desktop' && <DesktopCTA />}
                {device === 'ios' && <IosCTA />}
                {device === 'android' && <AndroidCTA />}
              </Box>

              {/* Trust line */}
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  mt: 2.5,
                  flexWrap: 'wrap',
                  rowGap: 1,
                  fontSize: 12,
                  color: C.slate500,
                }}
              >
                {[
                  'Free to use',
                  'Works with your GoodShowHQ account',
                  'COPPA-compliant',
                ].map(t => (
                  <Box
                    key={t}
                    component="span"
                    sx={{display: 'inline-flex', alignItems: 'center', gap: 0.5}}
                  >
                    <CheckCircleRounded
                      sx={{fontSize: 14, color: C.emerald500}}
                    />
                    {t}
                  </Box>
                ))}
              </Stack>
            </Box>

            {/* Phone mockup */}
            <Box
              sx={{
                gridColumn: {lg: 'span 2'},
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <PhoneMockup />
            </Box>
          </Box>

          {/*
            The phone app only REVIEWS — something has to be running
            24/7 to actually watch the feed and apply decisions. A
            parent who installs only the phone app and wonders why
            nothing happens is the failure this cross-link prevents.
          */}
          <Box
            sx={{
              mt: 5,
              display: 'flex',
              flexDirection: {xs: 'column', sm: 'row'},
              alignItems: {xs: 'flex-start', sm: 'center'},
              justifyContent: 'space-between',
              gap: 1.5,
              borderRadius: '16px',
              border: `1px solid ${C.slate200}`,
              bgcolor: C.white,
              px: 2.5,
              py: 2,
            }}
          >
            <Stack direction="row" spacing={1.25} sx={{alignItems: 'center'}}>
              <HomeAssistantIcon size={18} />
              <AppleIcon size={16} color={C.slate600} />
              <WindowsIcon size={16} color={C.slate600} />
              <LinuxIcon size={16} color={C.slate600} />
              <Typography sx={{ml: 0.5, fontSize: 13, color: C.slate600}}>
                Pair it with the Home Assistant add-on or the desktop app — that&apos;s
                what keeps watching when your phone is in your pocket.
              </Typography>
            </Stack>
            <Box
              component="a"
              href="#/downloads"
              sx={{
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                textDecoration: 'none',
                fontSize: 13,
                fontWeight: 700,
                color: C.indigo600,
                '&:hover': {color: C.indigo800},
              }}
            >
              Downloads
              <ArrowForwardRounded sx={{fontSize: 14}} />
            </Box>
          </Box>

          {/* Mobile-specific features */}
          <Box
            component="section"
            sx={{mt: 10, borderTop: `1px solid ${C.slate200}`, pt: 6}}
          >
            <Typography
              component="h2"
              sx={{
                textAlign: 'center',
                fontSize: 12,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: C.slate500,
              }}
            >
              What the mobile app does best
            </Typography>
            <Box
              sx={{
                mt: 4,
                display: 'grid',
                gridTemplateColumns: {xs: '1fr', md: 'repeat(3, minmax(0, 1fr))'},
                gap: 3,
              }}
            >
              {features.map(({Icon, title, body}) => (
                <Box key={title}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 40,
                      width: 40,
                      borderRadius: '12px',
                      bgcolor: C.indigo100,
                      color: C.indigo600,
                    }}
                  >
                    <Icon sx={{height: 20, width: 20}} />
                  </Box>
                  <Typography
                    component="h3"
                    sx={{
                      mt: 2,
                      fontSize: 16,
                      fontWeight: 800,
                      letterSpacing: '-0.01em',
                      color: C.slate900,
                    }}
                  >
                    {title}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 0.75,
                      fontSize: 14,
                      lineHeight: 1.6,
                      color: C.slate600,
                    }}
                  >
                    {body}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Container>

        {/* Footer */}
        <Box
          component="footer"
          sx={{borderTop: `1px solid ${C.slate200}`, bgcolor: C.white, py: 4}}
        >
          <Container maxWidth="lg" sx={{px: 3}}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: {xs: 'column', md: 'row'},
                alignItems: {xs: 'flex-start', md: 'center'},
                justifyContent: 'space-between',
                gap: 1.5,
              }}
            >
              <Logo />
              <Typography sx={{fontSize: 12, color: C.slate500}}>
                Not affiliated with YouTube or Google LLC. © 2026 GoodShowHQ,
                Inc.
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
